import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useReleaseNotes } from './useReleaseNotes';
import ReleaseNoteEditor, { type SaveIntent } from './components/ReleaseNoteEditor';
import ReleaseNoteRow from './components/ReleaseNoteRow';
import ResendDialog from './components/ResendDialog';
import ConfirmDialog from './components/ConfirmDialog';
import type { ReleaseNote, ReleaseNotePayload } from '../services/releaseNotes.service';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'published', label: 'Published' },
  { value: 'failed', label: 'Send failed' },
];

/**
 * Global Admin surface for authoring and sending release notes.
 *
 * List state and mutations live in useReleaseNotes; this component is layout,
 * dialog orchestration and confirmation prompts only.
 */
const ReleaseNotesPanel: React.FC = () => {
  const {
    notes,
    total,
    page,
    setPage,
    statusFilter,
    setStatusFilter,
    isLoading,
    error,
    pageSize,
    create,
    update,
    remove,
    publish,
    resend,
  } = useReleaseNotes();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<ReleaseNote | null>(null);
  const [resendTarget, setResendTarget] = useState<ReleaseNote | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReleaseNote | null>(null);
  const [publishTarget, setPublishTarget] = useState<ReleaseNote | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (note: ReleaseNote) => {
    setEditing(note);
    setEditorOpen(true);
  };

  const handleSave = async (payload: ReleaseNotePayload, intent: SaveIntent) => {
    const saved = editing ? await update(editing._id, payload) : await create(payload);
    // 'draft' and 'schedule' are fully expressed by the payload's publishAt;
    // only 'publish' needs the extra call that sends the email now.
    if (intent === 'publish' && saved?._id) {
      await publish(saved._id);
    }
  };

  const deleteWasSent =
    deleteTarget?.status === 'published' || deleteTarget?.status === 'failed';

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Release Notes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Announce product updates by email and as an in-app popup.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField
            select
            size="small"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 160 }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
            New release note
          </Button>
        </Box>
      </Box>

      {(error || actionError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {error ?? actionError}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Audience</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Delivery</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : notes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">
                    No release notes yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              notes.map((note) => (
                <ReleaseNoteRow
                  key={note._id}
                  note={note}
                  onEdit={openEdit}
                  onDelete={setDeleteTarget}
                  onPublish={setPublishTarget}
                  onResend={setResendTarget}
                />
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={(_, next) => setPage(next + 1)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[pageSize]}
        />
      </TableContainer>

      <ReleaseNoteEditor
        open={editorOpen}
        note={editing}
        onClose={() => setEditorOpen(false)}
        onSave={handleSave}
      />

      <ResendDialog
        open={Boolean(resendTarget)}
        note={resendTarget}
        onClose={() => setResendTarget(null)}
        onConfirm={async (failedOnly) => {
          if (resendTarget) await resend(resendTarget._id, failedOnly);
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete release note"
        message={`Delete "${deleteTarget?.title}"?`}
        warning={
          deleteWasSent
            ? 'This note has already been sent. Deleting removes it from this list and stops the in-app popup, but cannot recall the email.'
            : undefined
        }
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (deleteTarget) await remove(deleteTarget._id);
        }}
      />

      <ConfirmDialog
        open={Boolean(publishTarget)}
        title="Publish release note"
        message={`Publish "${publishTarget?.title}" now?`}
        warning={
          publishTarget?.sendEmail
            ? 'This emails the selected audience immediately.'
            : undefined
        }
        confirmLabel="Publish now"
        onClose={() => setPublishTarget(null)}
        onConfirm={async () => {
          if (publishTarget) await publish(publishTarget._id);
        }}
      />
    </Box>
  );
};

export default ReleaseNotesPanel;
